import React, { type JSX } from 'react';
import { Avatar, Stack, Typography, type SxProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomIcon from '../CustomIcon';
import { useNewCustomTranslations } from 'hooks';

type WebIcon = {
    name: string;
    family: string;
    style?: {
        color?: string;
        backgroundColor?: string;
    };
};

type EventListingItemProps = {
    icon?: {
        web?: WebIcon;
    };
    indicator?: string;
    within?: number | null;
    isNow?: boolean;
    type?: string;
    column?: 'status' | 'within' | 'type' | 'full';
    containerSx?: SxProps;
    testId?: string;
};

/**
 * Reusable EventListingItem component for displaying events with dynamic icons and indicators
 * Supports both web and mobile through icon.web and icon.mobile configuration
 */
const resolveIconColors = (webIcon?: WebIcon): { bgColor: string; iconColor: string } => {
    const iconStyle = webIcon?.style;
    return {
        bgColor: iconStyle?.backgroundColor ?? '#FFFFFF',
        iconColor: iconStyle?.color ?? '#727E84',
    };
};

const renderIconNode = (webIcon: WebIcon | undefined, bgColor: string, iconColor: string): JSX.Element | null =>
    webIcon ? (
        <Avatar sx={{ bgcolor: bgColor }}>
            <CustomIcon
                iconName={webIcon.name ?? ''}
                family={webIcon.family ?? ''}
                iconProps={{
                    sx: {
                        color: iconColor,
                    },
                }}
            />
        </Avatar>
    ) : null;

type TranslationFunction = (key: string) => string;

const renderWithinColumn = (
    isNow: boolean,
    withinDays: number,
    isSoon: boolean,
    t: TranslationFunction
): JSX.Element => (
    <Stack>
        <Typography
            variant="body2"
            sx={{
                color: '#737E85',
                fontFamily: 'Open Sans',
                fontWeight: 800,
                lineHeight: '100%',
                letterSpacing: '2px',
                textTransform: 'uppercase',
            }}
        >
            {isNow ? t('UPCOMING:NOW') : isSoon ? t('UPCOMING:SOON') : t('UPCOMING:WITHIN')}
        </Typography>
        {!isNow && !isSoon && (
            <Typography variant="body2" sx={{ color: '#737E85', fontWeight: 400 }}>
                {`${withinDays} ${t('UPCOMING:DAYS')}`}
            </Typography>
        )}
    </Stack>
);

const renderTypeColumn = (isNow: boolean, type?: string): JSX.Element => (
    <Typography sx={{ color: isNow ? '#E2721A' : '#424E54', fontWeight: 600 }}>{String(type ?? '')}</Typography>
);

const renderFullColumn = (
    containerSx: SxProps | undefined,
    normalizedIndicator: string,
    iconNode: JSX.Element | null,
    within: number | null | undefined,
    type?: string
): JSX.Element => (
    <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
            ...containerSx,
            position: 'relative',
        }}
    >
        {normalizedIndicator ? (
            <Stack
                sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: -2,
                    left: 0,
                    width: 6,
                    backgroundColor: normalizedIndicator,
                    zIndex: 100,
                }}
            />
        ) : null}
        {iconNode}
        <EventListingItem column="within" within={within} />
        <EventListingItem column="type" within={within} type={type} />
    </Stack>
);

const renderStatusColumn = (
    containerSx: SxProps | undefined,
    iconNode: JSX.Element | null,
    column: string,
    type?: string
): JSX.Element => (
    <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
            ...containerSx,
            position: 'relative',
        }}
    >
        {iconNode}
        {column !== 'status' && type && (
            <Typography sx={{ color: '#424E54', fontWeight: 600 }}>{String(type)}</Typography>
        )}
    </Stack>
);

export const EventListingItem = ({
    icon,
    indicator,
    within,
    isNow: isNowOverride,
    type,
    column = 'status',
    containerSx,
    testId,
}: EventListingItemProps): JSX.Element => {
    const { t } = useTranslation();
    useNewCustomTranslations('UPCOMING');
    const webIcon = icon?.web;
    const { bgColor, iconColor } = resolveIconColors(webIcon);
    const normalizedIndicator = typeof indicator === 'string' ? indicator.trim() : '';
    const parsedWithin = Number(within);
    const withinDays = Number.isFinite(parsedWithin) ? parsedWithin : 0;
    const isNow = typeof isNowOverride === 'boolean' ? isNowOverride : withinDays <= 0;
    const isSoon = !isNow && withinDays > 60;

    const iconNode = renderIconNode(webIcon, bgColor, iconColor);

    if (column === 'within') {
        return <div data-testid={testId}>{renderWithinColumn(isNow, withinDays, isSoon, t)}</div>;
    }

    if (column === 'type') {
        return <div data-testid={testId}>{renderTypeColumn(isNow, type)}</div>;
    }

    if (column === 'full') {
        return (
            <div data-testid={testId}>{renderFullColumn(containerSx, normalizedIndicator, iconNode, within, type)}</div>
        );
    }

    return <div data-testid={testId}>{renderStatusColumn(containerSx, iconNode, column, type)}</div>;
};
